module ContractProfitFade

open FSharpPlus
open Client
open Fable.React
open Fable.React.Props
open Model
open PortalLibs.Client.Util
open Fable.Core
open PortalLibs.Client.Components.Progress
open Fable.Core.JsInterop
open System
open Feliz

[<ImportDefault("react-chartkick")>]
let ReactChartkick: obj = jsNative

importAll "chartkick/chart.js"

[<ImportMember("chart.js")>]
let Chart: obj = jsNative

Chart?defaults?elements?point?radius <- 0

ReactChartkick?addAdapter Chart

let ProfitFadeChart =
  React.memo (
    "ProfitFadeChart",
    (fun (props: {| profitFadeEntries: ContractProfit list
                    Key: string |}) ->
      let revenueData =
        props.profitFadeEntries
        |> Seq.skipWhile (fun h -> h.Revenue = 0.0) 
        |>> fun h -> (h.Mth |> prettyPrintDateDashes, h.Revenue)
        |> Seq.toArray

      let gpPctData =
        props.profitFadeEntries
        |>> fun h -> (h.Mth |> prettyPrintDateDashes, Math.Round(h.GPPct * 100.0, 2))
        |> Seq.toArray

      let grossProfit =
        props.profitFadeEntries
        |>> fun h -> (h.Mth |> prettyPrintDateDashes, h.Revenue - h.Cost)
        |> Seq.toArray

      let inputProps =
        {| thousands = ","
           height = "400px"
           curve = false
           data =
            [| {| name = "Revenue"
                  data = revenueData
                  dataset = {| yAxisID = "y" |}
                  color = "#00cc00" |}
               {| name = "GP"
                  data = gpPctData
                  dataset = {| yAxisID = "y2" |}
                  color = "#1a75ff" |}
               {| name = "Gross Profit"
                  data = grossProfit
                  dataset = {| yAxisID = "y" |}
                  color = "#ff6347" |} |]
           library =
            {| responsive = true
               hover = {| mode = "nearest"; intersect = true |}
               scales =
                {| y =
                    {| display = true
                       ticks = {| display = true; maxTicksLimit = 6 |}
                       title = {| display = true; text = "Revenue & Gross Profit" |}
                       gridLines = {| display = true |} |}
                   y2 =
                    {| display = true
                       ticks =
                        {| display = true
                           callback = fun value -> value + "%" |}
                       title = {| display = true; text = "GP%" |}
                       id = "y-axis-2"
                       position = "right"
                       gridLines = {| drawOnChartArea = false |} |}
                   x =
                    {| display = true
                       gridLines =
                        {| display = true
                           color = "rgba(0, 0, 0, 0.1)"
                           lineWidth = 1
                           drawOnChartArea = 1 |} |} |}

               animation =
                {| duration = 1500
                   easing = "easeOutElastic" |}
               plugins =
                {| legend = {| position = "bottom" |}
                   tooltip =
                    {| mode = "index"
                       intersect = false
                       displayColors = true
                       padding = 10
                       bodySpacing = 4
                       callbacks =
                        {| label =
                            fun item ->
                              let label, data =
                                match item?datasetIndex with
                                | 0 -> ("    Revenue",
                                        $"%15s{(DisplayNumber.AsCurrency.std: float -> string) item?raw}")
                                | 1 -> ("          GP%", sprintf "%17.2f%%" item?raw)
                                | _ ->
                                  ("Gross profit",
                                   $"%15s{(DisplayNumber.AsCurrency.std: float -> string) item?raw}")

                              $"%s{label}:%s{data}"
                           title = fun (item: obj array) -> $"%22s{item[0]?label}" |} |} |} |} |}

      ofImport "LineChart" "react-chartkick" inputProps []),
    (fun (props: {| profitFadeEntries: ContractProfit list
                    Key: string |}) -> $"ProfitFadeChart-%s{props.Key}")
  )

let profitFadeValues (profitFadeOpt: ContractProfitFade option) =
  match profitFadeOpt with
  | None ->
    div [ Style [
            TextAlign TextAlignOptions.Center
          ] ] [
      str "Profit fade details not available."
    ]
  | Some profitFade ->
    div [ Style [
            Display DisplayOptions.Flex
            FlexWrap "wrap"
            AlignSelf AlignSelfOptions.FlexEnd
            TextAlign TextAlignOptions.Left
            PaddingBottom "10px"
            MarginLeft "auto"
            MarginRight "auto"
            JustifyContent "space-around"
            PaddingLeft "35px"
          ] ] [
      textFieldReadOnlyStyles
        (Some 150)
        []
        "Contract Amount"
        (profitFade.ContractAmount
         |> DisplayNumber.AsCurrency.std)
      textFieldReadOnlyStyles
        (Some 150)
        []
        "Profit delta over life of contract"
        (profitFade.ProfitLifeDelta
         |> DisplayNumber.AsCurrency.std)
      textFieldReadOnlyStyles
        (Some 150)
        []
        "Profit delta over last 3 months"
        (profitFade.Profit3MthDelta
         |> DisplayNumber.AsCurrency.std)
      progressWithLabel "Current Contract GP%" "" (profitFade.CurrentGP * 100.0)
      progressWithLabel "GP delta" "life of contract" (profitFade.GPLifeDelta * 100.0)
      progressWithLabel "GP delta" "last 3 months" (profitFade.GP3MthDelta * 100.0)
    ]

let View contractProfitFades (contract: Contract) =
  let profitFadeView (profitFadeOpt, jobProfits) =
    div [ Style [ PaddingBottom "50px" ] ] [
      div [ Style [
              PaddingBottom "25px"
              PaddingTop "15px"
            ] ] [
        profitFadeValues profitFadeOpt
      ]
      ProfitFadeChart
        {| profitFadeEntries = jobProfits
           Key = sprintf "FadeChart-%i" contract.Id |}
    ]

  loadingMessageOrContent
    "Loading Profit Fade..."
    profitFadeView
    (Map.tryFind contract.Id contractProfitFades)